import { Component, Inject, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { finalize } from 'rxjs/operators';
import { MapService } from 'src/app/shared/components/map/map.service';
import { WindowService } from 'src/app/shared/components/window/window.service';
import { Vertex } from '../../vertexes/state/vertex.model';
import { VertexQuery } from '../../vertexes/state/vertex.query';
import { Building, BuildingBlock, BuildingProperties, createBuilding, createBuildingBlock, createBuildingProperties } from '../state/building.model';
import { BuildingService } from '../state/building.service';

@Component({
  selector: 'app-building-form',
  templateUrl: './building-form.component.html',
  styleUrls: ['./building-form.component.scss']
})
export class BuildingFormComponent implements OnInit {

  isLoading: boolean;
  editionMode: boolean;
  removeConfirmated: boolean;

  formBuilding: FormGroup;
  blocksForm: FormArray;

  vertex: Vertex;

  constructor(
    @Inject(MAT_DIALOG_DATA) public building: Building,
    private formBuilder: FormBuilder,
    private snakBar: MatSnackBar,
    private windowService: WindowService,
    private buildingService: BuildingService,
    private vertexQuery: VertexQuery,
    private mapService: MapService
  ) {

    if (this.building.id) {
      this.editionMode = true;
    }
  }

  ngOnInit(): void {
    this.buildingService.updateUI({ visible: true });
    this.mapService.map$.subscribe(m => m.setLayoutProperty('mitra-building-conn', 'visibility', 'visible'));

    if (this.editionMode) {
      this.editionMode = true;
      this.buildingService.getFull(this.building.id)
        .pipe(finalize(() => this.isLoading = false))
        .subscribe(b => {
          this.building = b;
          this.vertex = this.vertexQuery.getEntity(this.building.vertex);
          this.createForm();
        });
    } else {
      this.createForm();
    }
  }

  private createForm() {
    const properties = createBuildingProperties(this.building.properties);

    this.loadBlockForms(properties.blocks);

    this.formBuilding = this.formBuilder.group({
      street_name: [properties.street_name],
      number: [properties.number],
      complement: [properties.complement],
      occupancy: [properties.occupancy, Validators.required],
      orientation: [properties.orientation, Validators.required],
      name: [properties.name],
      type: [properties.type, Validators.required],
      residential_units: [properties.residential_units, Validators.min(0)],
      nonresidential_units: [properties.nonresidential_units, Validators.min(0)],
      blocks: this.blocksForm
    },
      {
        validators: [
          residentialUnitsValidation,
          nonResidentialUnitsValidation,
          blockResidentialUnitsValidation,
          blockNonResidentialUnitsValidation
        ]
      }
    );

    this.formBuilding.valueChanges.subscribe((values) => this.valueChanges(values));
  }

  private valueChanges(properties: BuildingProperties): void {
    if (properties.orientation === 'Vertical') {
      if (properties.blocks.length === 0) {
        this.addBlock();
      }
    } else {
      this.blocksForm.clear();
    }
  }

  private loadBlockForms(blocks: BuildingBlock[]): void {
    this.blocksForm = this.formBuilder.array([]);
    blocks.forEach(block => {
      this.blocksForm.push(this.createBlockForm(block));
    });
  }

  private createBlockForm(block: BuildingBlock): FormGroup {
    return this.formBuilder.group({
      name: [block.name, Validators.required],
      floors: [block.floors, [Validators.required, Validators.min(1)]],
      residential_units: [block.residential_units, [Validators.required, Validators.min(0)]],
      nonresidential_units: [block.nonresidential_units, [Validators.required, Validators.min(0)]]
    });
  }

  addBlock(): void {
    this.blocksForm.push(this.createBlockForm(createBuildingBlock({})));
  }

  deleteBlock(i: number): void {
    this.blocksForm.removeAt(i);
  }

  minus(form: AbstractControl, controlName: string) {
    const newValue = form.value[controlName] - 1;
    form.patchValue({ [controlName]: newValue });
  }

  plus(form: AbstractControl, controlName: string) {
    const newValue = form.value[controlName] + 1;
    form.patchValue({ [controlName]: newValue });
  }

  save() {
    if (this.editionMode) {
      this.update();
    } else {
      this.insert();
    }
  }

  remove() {
    if (this.removeConfirmated) {
      this.buildingService.remove(this.building.id).subscribe(
        v => {
          this.snakBar.open($localize`Building "${this.building.id}" update`, null, { duration: 2000 });
          this.windowService.close(this);
        },
        error => this.snakBar.open($localize`Error on Building "${this.building.id}" update`, 'CLOSE'),
      );
    }
    this.removeConfirmated = true;
  }

  private insert() {
    const building = this.formToData();

    this.buildingService.add(building).subscribe(
      b => {
        this.snakBar.open($localize`Building "${b.id}" created`, null, { duration: 2000 });
        this.windowService.close(this);
      },
      error => this.snakBar.open($localize`Error on Building creation`, 'CLOSE'),
    );
  }

  private update() {
    const building = this.formToData();
    this.buildingService.update(building.id, building).subscribe(
      v => {
        this.snakBar.open($localize`Building "${building.id}" update`, null, { duration: 2000 });
        this.windowService.close(this);
      },
      error => this.snakBar.open($localize`Error on Building "${building.id}" update`, 'CLOSE'),
    );
  }

  private formToData(): Building {
    const building = createBuilding(this.building);
    building.properties = {
      ...this.formBuilding.value
    };
    return building;
  }

}

const residentialUnitsValidation: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const properties = control.value as BuildingProperties;

  if (
    properties.occupancy === 'Collective' &&
    properties.type !== 'NonResidential' &&
    properties.orientation === 'Horizontal' &&
    properties.residential_units < 1
  ) {
    return {
      residentialUnitsMin: true
    };
  }
};

const nonResidentialUnitsValidation: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const properties = control.value as BuildingProperties;

  if (
    properties.occupancy === 'Collective' &&
    properties.type !== 'Residential' &&
    properties.orientation === 'Horizontal' &&
    properties.nonresidential_units < 1
  ) {
    return {
      nonResidentialUnitsMin: true
    };
  }
};

const blockResidentialUnitsValidation: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const properties = control.value as BuildingProperties;

  if (properties.occupancy === 'Collective' && properties.type !== 'NonResidential') {
    const errors: ValidationErrors = {};
    properties.blocks.forEach((b, i) => {
      if (b.residential_units < 1) {
        errors[`block_${i}_residentialUnitsMin`] = true;
      }
    });
    return errors;
  }
};

const blockNonResidentialUnitsValidation: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const properties = control.value as BuildingProperties;

  if (properties.occupancy === 'Collective' && properties.type !== 'Residential') {
    const errors: ValidationErrors = {};
    properties.blocks.forEach((b, i) => {
      if (b.nonresidential_units < 1) {
        errors[`block_${i}_nonResidentialUnitsMin`] = true;
      }
    });
    return errors;
  }
};
